/**
 * DataReliabilityModal — public-facing diagnostic for the Data Rel KPI.
 *
 * DEV-1080, Chunk B. Opens when any viewer clicks the Data Rel. shape
 * on the Mini-Project Progress tab.
 *
 * Per Skip's 2026-05-19 decision (see computeKpiStatus.ts §"Data
 * Reliability"), this KPI is "staleness only" in v1: green ≤ 3 days,
 * yellow 4–7 days, red > 7 days, grey if no timestamp. This modal is a
 * read-only explainer of why the current color is what it is — no edit
 * surface, because the value is auto-derived from the most recent
 * sub-entity write.
 *
 * Composition:
 *   - Header: "Data Reliability" + current color chip + close ×
 *   - Big number: days since last update
 *   - Threshold band visualization: a horizontal bar with the three
 *     zones (green/yellow/red) and a marker showing where this
 *     project sits.
 *   - "Last update" timestamp with which source field provided it
 *   - Lead-only hint: how to refresh the timestamp (post a status, edit
 *     a sub-entity).
 *
 * Strings localized via `pages:miniProject.kpiModals.dataRel.*`
 * (Chunk D Phase 4 sweep, 2026-05-22).
 */

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProject } from "../../api/miniProjects";
import styles from "./DataReliabilityModal.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  miniProject: MiniProject;
  isLead: boolean;
}

/* Thresholds match computeKpiStatus.ts. Kept inline so this component
 * is self-documenting — but the canonical source is computeKpiStatus.
 * If you change one, change both. */
const GREEN_MAX_DAYS = 3;
const YELLOW_MAX_DAYS = 7;
/* Visual cap for the bar — anything past this draws at the rightmost
 * position. Doesn't change the color rule, just the chart geometry. */
const CHART_MAX_DAYS = 14;

/* ── Helpers ───────────────────────────────────────────────────── */

/**
 * Pick the freshest timestamp source available on the mini-project.
 * Returns the ISO string and an i18n key for the source label so the
 * caller can resolve via t() at render time (sources are localized).
 */
function pickTimestamp(mp: MiniProject): { iso: string; sourceKey: string } | null {
  if (mp.lastDataUpdateAt) return { iso: mp.lastDataUpdateAt, sourceKey: "miniProject.kpiModals.dataRel.source.subEntity" };
  if (mp.updatedAt) return { iso: mp.updatedAt, sourceKey: "miniProject.kpiModals.dataRel.source.project" };
  return null;
}

function daysBetween(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, ms / 86400000);
}

function classify(days: number | null): {
  color: "green" | "yellow" | "red" | "grey";
  labelKey: string;
} {
  if (days === null) return { color: "grey", labelKey: "miniProject.kpiModals.dataRel.band.noTimestamp" };
  if (days <= GREEN_MAX_DAYS) return { color: "green", labelKey: "miniProject.kpiModals.dataRel.band.fresh" };
  if (days <= YELLOW_MAX_DAYS) return { color: "yellow", labelKey: "miniProject.kpiModals.dataRel.band.gettingStale" };
  return { color: "red", labelKey: "miniProject.kpiModals.dataRel.band.stale" };
}

function formatLongDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/* ── Component ─────────────────────────────────────────────────── */

const DataReliabilityModal: React.FC<Props> = ({ open, onClose, miniProject, isLead }) => {
  const { t } = useAppTranslation("pages");
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const ts = pickTimestamp(miniProject);
  const days = ts ? daysBetween(ts.iso) : null;
  const band = classify(days);

  if (!open) return null;

  // Marker position on the bar: 0..1 fraction of CHART_MAX_DAYS.
  const markerPct =
    days === null ? 0 : Math.min(1, days / CHART_MAX_DAYS);
  const greenEnd = (GREEN_MAX_DAYS / CHART_MAX_DAYS) * 100;
  const yellowEnd = (YELLOW_MAX_DAYS / CHART_MAX_DAYS) * 100;

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t("miniProject.kpiModals.dataRel.title")}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.title}>{t("miniProject.kpiModals.dataRel.title")}</span>
            <span className={`${styles.bandChip} ${styles[`band_${band.color}`]}`}>
              <span className={styles.bandDot} aria-hidden="true" />
              {t(band.labelKey)}
            </span>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("miniProject.kpiModals.dataRel.close")}>
            ×
          </button>
        </header>

        <div className={styles.body}>
          {/* Big number: days since last update */}
          <div className={styles.bigNumber}>
            <div className={`${styles.bigNumberValue} ${styles[`band_${band.color}_text`]}`}>
              {days === null ? "—" : days < 1 ? "< 1" : Math.floor(days)}
            </div>
            <div className={styles.bigNumberLabel}>
              {days === null
                ? t("miniProject.kpiModals.dataRel.bigLabel.noTimestamp")
                : Math.floor(days) === 1
                  ? t("miniProject.kpiModals.dataRel.bigLabel.day")
                  : t("miniProject.kpiModals.dataRel.bigLabel.days")}
            </div>
          </div>

          {/* Threshold band bar */}
          <div className={styles.barWrap}>
            <div className={styles.barTitle}>{t("miniProject.kpiModals.dataRel.thresholdBand")}</div>
            <div className={styles.bar}>
              <div
                className={`${styles.barZone} ${styles.barZoneGreen}`}
                style={{ inlineSize: `${greenEnd}%` }}
              >
                <span className={styles.barZoneLabel}>{t("miniProject.kpiModals.dataRel.zone.green", { max: GREEN_MAX_DAYS })}</span>
              </div>
              <div
                className={`${styles.barZone} ${styles.barZoneYellow}`}
                style={{ inlineSize: `${yellowEnd - greenEnd}%` }}
              >
                <span className={styles.barZoneLabel}>
                  {t("miniProject.kpiModals.dataRel.zone.yellow", { lo: GREEN_MAX_DAYS + 1, hi: YELLOW_MAX_DAYS })}
                </span>
              </div>
              <div
                className={`${styles.barZone} ${styles.barZoneRed}`}
                style={{ inlineSize: `${100 - yellowEnd}%` }}
              >
                <span className={styles.barZoneLabel}>{t("miniProject.kpiModals.dataRel.zone.red", { min: YELLOW_MAX_DAYS + 1 })}</span>
              </div>
              {days !== null && (
                <div
                  className={styles.barMarker}
                  style={{ insetInlineStart: `${markerPct * 100}%` }}
                  aria-label={t("miniProject.kpiModals.dataRel.markerAria", { days: Math.floor(days) })}
                />
              )}
            </div>
          </div>

          {/* Last update detail */}
          {ts && (
            <div className={styles.detail}>
              <div className={styles.detailLabel}>{t("miniProject.kpiModals.dataRel.lastUpdate")}</div>
              <div className={styles.detailValue}>{formatLongDateTime(ts.iso)}</div>
              <div className={styles.detailSource}>{t("miniProject.kpiModals.dataRel.source.label", { source: t(ts.sourceKey) })}</div>
            </div>
          )}

          {/* Lead hint */}
          {isLead && (
            <div className={styles.leadHint}>
              {t("miniProject.kpiModals.dataRel.leadHint")}
            </div>
          )}

          {!isLead && days !== null && days > YELLOW_MAX_DAYS && (
            <div className={styles.publicHint}>
              {t("miniProject.kpiModals.dataRel.publicHint")}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DataReliabilityModal;
